import { Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { ViewLayoutModel } from './models/view-layout.model';
import { ViewState } from './state/view.state';
import { ViewBreakpoint } from './models/view-breakpoint.enum';
import { ViewLayoutStatus } from './models/view-layout-status.enum';
import { ViewLayout } from './models/view-layout.enum';
import { StoryLabelsState } from '../story/state/labels/story.labels.state';
import { StoryChaptersState } from '../story/state/chapters/story.chapters.state';
import { get, trim } from 'lodash';
import { environment } from 'src/environments/environment';
import { EnvironmentApplication } from 'src/environments/models/environment-application';

@Injectable({
  providedIn: 'root'
})
export class LayoutService {
  layout: ViewLayoutModel = null;
  breakpoint: ViewBreakpoint = null;

  constructor(
    private store: Store
  ) { }

  getBase() {
    return {
      layout: this.store.selectSnapshot(ViewState.getLayout),
      breakpoint: this.store.selectSnapshot(ViewState.getBreakpoint),
      loaderActive: this.store.selectSnapshot(ViewState.getLoaderActive)
    };
  }

  isDrawerVisible() {
    const { loaderActive, layout, breakpoint } = this.getBase();

    // Hide when loader is active
    if (loaderActive) {
      return false;
    }

    // Large breakpoint, never show
    if (breakpoint === ViewBreakpoint.Large) {
      return false;
    }

    // Show for loft & studio
    if (environment.application !== EnvironmentApplication.Viewer) {
      return true;
    }
    
    // Default layout (description)
    const defaultDrawerVisible = layout.drawer.enable && this.doesContentShow();

    // Medium & Small breakpoint
    // Show drawer when enabled
    // Show drawer when disabled, but tools/info modes active
    const visible = defaultDrawerVisible
      || layout.tools.status === ViewLayoutStatus.Show
      || layout.nodeInfo.status === ViewLayoutStatus.Show
      || layout.labelInfo.status === ViewLayoutStatus.Show;
    
    return visible;
  }

  isColumnVisible() {
    const { loaderActive, layout, breakpoint } = this.getBase();

    // Hide when loader is active
    if (loaderActive) {
      return false;
    }

    // Large breakpoint, never show
    if (breakpoint !== ViewBreakpoint.Large) {
      return false;
    }

    // Hide on collapse
    if (layout.column.status === ViewLayoutStatus.Collapse) {
      return false;
    }

    // Always show for loft & studio
    if (environment.application !== EnvironmentApplication.Viewer) {
      return true;
    }

    // Default layout (description)
    return layout.column.enable && this.doesContentShow();
  }
  
  isColumnToggleVisible() {
    const { loaderActive, layout, breakpoint } = this.getBase();

    // Hide when loader is active
    if (loaderActive) {
      return false;
    }

    // Large breakpoint, never show
    if (breakpoint !== ViewBreakpoint.Large) {
      return false;
    }

    return layout.column.enable && this.doesContentShow();
  }

  isToolsVisible() {
    const { loaderActive, layout, breakpoint } = this.getBase();

    // Hide when loader is active
    if (loaderActive) {
      return false;
    }

    return this.isLayoutVisible(ViewLayout.Tools);
  }

  isToolbarVisible() {
    const { loaderActive, layout, breakpoint } = this.getBase();

    // Hide when loader is active
    if (loaderActive) {
      return false;
    }

    return (breakpoint === ViewBreakpoint.Large) && layout.menu.enable;
  }

  isNavbarVisible() {
    const { loaderActive, layout, breakpoint } = this.getBase();

    // Hide when loader is active
    if (loaderActive) {
      return false;
    }

    // Navbar is part of toolbar at large, never show
    if (breakpoint === ViewBreakpoint.Large) {
      return false;
    }

    return layout.pagination.enable || layout.media.enable;
  }

  isLogoVisible() {
    const { layout } = this.getBase();
    return layout.logo.enable;
  }

  isCameraVisible() {
    const { loaderActive, layout } = this.getBase();

    // Hide when loader is active
    if (loaderActive) {
      return false;
    }

    return layout.camera.enable;
  }

  isPaginationVisible() {
    const { loaderActive, breakpoint, layout } = this.getBase();
    
    // Hide when loader is active
    if (loaderActive) {
      return false;
    }

    // Only show pagination on large
    // On medium and small, navbar component contains pagination
    if (breakpoint !== ViewBreakpoint.Large) {
      return false;
    }

    return layout.pagination.enable;
  }

  isMenuVisible() {
    const { loaderActive, layout, breakpoint } = this.getBase();

    // Hide when loader is active
    if (loaderActive) {
      return false;
    }
    
    // Large breakpoint, never show
    if (breakpoint === ViewBreakpoint.Large) {
      return false;
    }

    // Media / Small, defer to enable
    return layout.menu.enable;
  }

  isNodeInfoColumnVisible() {
    const { loaderActive, layout, breakpoint } = this.getBase();

    // Hide when loader is active
    if (loaderActive) {
      return false;
    }

    if (breakpoint === ViewBreakpoint.Large && this.isLayoutVisible(ViewLayout.NodeInfo)) {
      return true;
    }
    
    // Medium / Small, defer to drawer enabled
    if (breakpoint !== ViewBreakpoint.Large) {
      return !layout.drawer.enable && this.isLayoutVisible(ViewLayout.NodeInfo);
    }
  }

  isNodeInfoVisible(location: 'drawer' | 'column') {
    const { loaderActive, layout, breakpoint } = this.getBase();

    // Hide when loader is active
    if (loaderActive) {
      return false;
    }

    // Large, never show
    if (breakpoint === ViewBreakpoint.Large && location === 'column') {
      return this.isLayoutVisible(ViewLayout.NodeInfo);
    }

    // Medium / Small, drawer and column
    if (breakpoint !== ViewBreakpoint.Large && location === 'drawer') {
      return this.isLayoutVisible(ViewLayout.NodeInfo);
    }

    return false;
  }

  isSearchColumnVisible() {
    const { loaderActive, layout, breakpoint } = this.getBase();

    // Hide when loader is active
    if (loaderActive) {
      return false;
    }
    
    return this.isLayoutVisible(ViewLayout.Search);
  }

  isVisible(layout: ViewLayout) {
    switch (layout) {
      case ViewLayout.Column:
        return this.isColumnVisible();

      case ViewLayout.ColumnToggle:
          return this.isColumnToggleVisible();

      case ViewLayout.Drawer:
        return this.isDrawerVisible();

      case ViewLayout.Tools:
        return this.isToolsVisible();

      case ViewLayout.Toolbar:
        return this.isToolbarVisible();

      case ViewLayout.Logo:
        return this.isLogoVisible();

      case ViewLayout.Camera:
        return this.isCameraVisible();

      case ViewLayout.Pagination:
        return this.isPaginationVisible();

      case ViewLayout.Menu:
        return this.isMenuVisible();

      case ViewLayout.Search:
        return this.isSearchColumnVisible();

      case ViewLayout.Navbar:
        return this.isNavbarVisible();

      default:
        return this.isLayoutVisible(layout);

    }
  }

  isLayoutVisible(layout: ViewLayout) {
    const _layout = this.store.selectSnapshot(ViewState.getLayout);
    const showLoader = this.store.selectSnapshot(ViewState.getLoaderActive);

    const el = _layout[layout];
    
    if (!el) {
      console.warn('Attempting to check visibility of a non-existent element: ${element}');
      return false;
    }


    return !showLoader && 
      _layout[layout].enable && 
      _layout[layout].status !== ViewLayoutStatus.Hide;
  }

  isLayoutEnabled(layout) {
    const _layout = this.store.selectSnapshot(ViewState.getLayout);
    return _layout[layout].enable;
  }

  // Checks if any content will actually show in the column or drawer
  private doesContentShow() {
    const { layout } = this.getBase();
    const labels = this.store.selectSnapshot(StoryLabelsState.getChapterLabels);
    const chapterCount = this.store.selectSnapshot(StoryChaptersState.getChapterCount);
    const description = get(this.store.selectSnapshot(StoryChaptersState.getSelectedChapter), 'info.description');

    return (layout.labelList.enable && labels.length > 0) || 
      (layout.chapterList.enable && chapterCount > 1) ||
      (description != null && trim(description).length > 0);
  }
}
